import { useCallback, useState } from 'react';
import { Navigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Icon } from '@framework/ui/atoms/Icon/Icon';
import { ExternalLink, FlatButton } from '@framework/ui/atoms';
import { useCurrentUserId, usePendingRegistration } from '@framework/auth';
import type { RegistrationDraft } from '@framework/auth';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { trpcClient } from '@framework/trpcClient';
import { useStoreRequestPath } from '@user/Login/useStoreRequestPath';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';

/**
 * Firebase 認証済みだが Balus ユーザー未作成(pendingRegistration)の状態を受けて、
 * ユーザー登録を完了させるページ。
 *
 * - 登録が完了して authenticated になったら requestPath へ戻る(招待時は招待ページへ戻り、
 *   state.signUpFinished により招待ページ側が自動でグループ参加処理を行う)。
 * - pendingRegistration でない直接アクセス時はトップへリダイレクトする。
 */
export const FirebaseSignUpPage: React.FC<unknown> = () => {
  const currentUserId = useCurrentUserId();
  const draft = usePendingRegistration();
  const { fetch: fetchRequestPath } = useStoreRequestPath();

  // 登録完了(authenticated)後は元のページ(requestPath)へ戻す。
  if (currentUserId) {
    const requestPath = fetchRequestPath();
    // Cookie 由来で改ざんされうるため、同一オリジン内のパス(`/` 始まり、`//` は除く)以外はトップに振り替える
    const safePath = requestPath.startsWith('/') && !requestPath.startsWith('//') ? requestPath : '/';
    // /login に戻すとログイン画面でループしうるためトップに振り替える(既存 OAuth 経路と同様)
    const nextPath = safePath === '/login' || safePath.startsWith('/login?') ? '/' : safePath;
    return <Navigate to={nextPath} state={{ signUpFinished: true }} replace />;
  }

  // pendingRegistration でない(直接アクセス等)場合はトップへ
  if (!draft) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex w-screen flex-col items-center justify-center pt-12">
      <div className="pb-12">
        <Icon name="balus" color="brand" size="brand-xl" />
      </div>

      <div className="flex flex-col rounded-lg border border-brand p-8" style={{ width: '600px' }}>
        {!draft.emailVerified && draft.email ? (
          <EmailVerificationForm draft={draft} />
        ) : (
          <FirebaseSignUpForm draft={draft} />
        )}
      </div>
    </div>
  );
};

type FormProps = {
  draft: RegistrationDraft;
};

const EmailVerificationForm: React.FC<FormProps> = ({ draft }: FormProps) => {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  const continueUrl = `${window.location.origin}${UserPagePathBuilder.firebaseSignUpPage()}`;

  const handleSend = useCallback(async () => {
    setSending(true);
    try {
      await FirebaseAuthenticationClient.sendEmailVerification(continueUrl);
      setSent(true);
    } catch (error) {
      console.error(error);
      toast.error(t('auth.signUp.emailVerification.sendError'));
    } finally {
      setSending(false);
    }
  }, [continueUrl, t]);

  const handleCheck = useCallback(async () => {
    setChecking(true);
    try {
      await FirebaseAuthenticationClient.reloadCurrentUser();
      if (!FirebaseAuthenticationClient.isEmailVerified()) {
        toast.error(t('auth.signUp.emailVerification.notVerifiedError'));
        return;
      }
      // emailVerified が true になっていれば forceRefreshToken で onIdTokenChanged をトリガーし、
      // AuthStateLoader が draft.emailVerified を更新してこのコンポーネントを FirebaseSignUpForm に切り替える。
      await FirebaseAuthenticationClient.forceRefreshToken();
    } catch (error) {
      console.error(error);
      toast.error(t('auth.signUp.emailVerification.notVerifiedError'));
    } finally {
      setChecking(false);
    }
  }, [t]);

  const handleCancel = useCallback(async () => {
    try {
      await FirebaseAuthenticationClient.signOut();
    } catch (error) {
      console.error(error);
    }
  }, []);

  return (
    <>
      <div className="text-2xl font-bold">{t('auth.signUp.emailVerification.title')}</div>

      <div className="pt-4 text-gray-700">{t('auth.signUp.emailVerification.description')}</div>

      <div className="pt-4 font-bold">{t('auth.signUp.emailLabel')}</div>
      <input
        type="text"
        className="w-full cursor-not-allowed rounded-sm border border-gray-500 bg-gray-200 p-2 focus:outline-none"
        defaultValue={draft.email ?? ''}
        disabled
      />

      {sent && <div className="pt-4 text-sm text-green-700">{t('auth.signUp.emailVerification.sentMessage')}</div>}

      <div className="flex flex-row justify-between gap-2 pt-8">
        <FlatButton color="black" onClick={handleCancel}>
          {t('common.action.cancel')}
        </FlatButton>
        <div className="flex flex-row gap-2">
          <FlatButton color="black" onClick={handleSend} disabled={sending} loading={sending}>
            {sent ? t('auth.signUp.emailVerification.resendButton') : t('auth.signUp.emailVerification.sendButton')}
          </FlatButton>
          <FlatButton color="brand" onClick={handleCheck} disabled={checking} loading={checking}>
            {t('auth.signUp.emailVerification.checkButton')}
          </FlatButton>
        </div>
      </div>
    </>
  );
};

const FirebaseSignUpForm: React.FC<FormProps> = ({ draft }: FormProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(draft.displayName ?? '');
  const [processing, setProcessing] = useState(false);
  const isValid = name.length > 0;

  const handleChangeName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    setProcessing(true);

    try {
      const { token } = await trpcClient.user.auth.signUpWithFirebaseAuth.mutate({
        signUpMode: draft.signUpMode,
        name,
      });
      await FirebaseAuthenticationClient.signInWithCustomToken(token);
      // 成功時は onIdTokenChanged → authenticated 遷移で画面が切り替わるため、
      // processing は解除せず処理中表示を維持する。
      // (同一 uid での再サインインになるため onAuthStateChanged は発火しない点に注意)
    } catch (error) {
      console.error(error);
      toast.error(t('auth.signUp.processingError'));
      setProcessing(false);
    }
  }, [draft.signUpMode, isValid, name, t]);

  // 登録を中断する場合は Firebase からサインアウトして未ログイン状態に戻す。
  const handleCancel = useCallback(async () => {
    try {
      await FirebaseAuthenticationClient.signOut();
    } catch (error) {
      console.error(error);
      toast.error(t('auth.signUp.processingError'));
    }
  }, [t]);

  return (
    <>
      <div className="text-2xl font-bold">{t('auth.signUp.title')}</div>

      <div className="pt-8 font-bold">{t('auth.signUp.nameLabel')}</div>
      <input
        type="text"
        className="w-full rounded-sm border border-gray-500 p-2 focus:outline-none"
        value={name}
        onChange={handleChangeName}
      />

      <div className="pt-8 font-bold">{t('auth.signUp.emailLabel')}</div>
      <input
        type="text"
        className="w-full cursor-not-allowed rounded-sm border border-gray-500 bg-gray-200 p-2 focus:outline-none"
        defaultValue={draft.email ?? ''}
        disabled
      />

      <div className="pt-8">
        <Trans
          i18nKey="auth.signUp.terms"
          components={{
            termsLink: <ExternalLink href="https://levii.co.jp/legal/terms-of-service/" />,
            privacyLink: <ExternalLink href="https://levii.co.jp/legal/privacy-policy/" />,
          }}
        />
      </div>

      <div className="flex flex-row justify-end gap-2 pt-8">
        <FlatButton color="black" onClick={handleCancel} disabled={processing}>
          {t('common.action.cancel')}
        </FlatButton>
        <FlatButton color="brand" onClick={handleSubmit} disabled={!isValid || processing} loading={processing}>
          {t('auth.signUp.submitButton')}
        </FlatButton>
      </div>
    </>
  );
};
